import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

import { env } from '@/app/config/env';
import { AppProviders } from '@/app/providers/app-providers';
import { queryClient } from '@/app/providers/query-client';
import { router } from '@/app/router/routes';
import { configureAuth } from '@/features/auth';

import '@/app/theme/theme.css';

// Register the auth callbacks on the HTTP client before anything can issue a
// request. This is startup wiring, not a rendering concern, so it happens here
// rather than inside a component.
configureAuth(queryClient);

/**
 * Start Mock Service Worker before mounting, when enabled.
 *
 * MSW mocks FEATURE DATA only (dashboard, orders, brands, categories) while
 * those features wait for their real endpoints. Authentication is never
 * mocked: login, refresh, logout and /admin/me have no handlers, so with
 * `onUnhandledRequest: 'bypass'` they always reach the real UT backend.
 *
 * Awaiting the worker matters: if React mounts first, the queries fired by the
 * initial render race the worker's registration and some of them escape to the
 * network unmocked.
 */
async function enableMocking() {
  if (!env.enableMsw) return;

  const { worker } = await import('./mocks/browser');
  return worker.start({
    onUnhandledRequest: 'bypass',
    quiet: true,
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html.');
}

void enableMocking().then(() => {
  createRoot(rootElement).render(
    <StrictMode>
      <AppProviders>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  );
});
